// S14 — Settings BE client.
//
// BE endpoints:
//   GET  /api/v1/admin/settings                — list (filter: category, isPublic)
//   GET  /api/v1/admin/settings/{key}          — single by key
//   POST /api/v1/admin/settings                — create new key
//   PUT  /api/v1/admin/settings/{key}          — update by key (rowVersion concurrency)
//   DELETE /api/v1/admin/settings/{key}        — delete
//   GET  /api/v1/public/settings               — anonymous public-safe subset

import { http, unwrap, unwrapList, type ActionResponse } from './http';
import {
  SETTING_VALUE_TYPE_TO_NUMBER,
  settingValueTypeFromNumber,
  type AppSetting,
  type CreateSettingInput,
  type PublicSetting,
  type SystemSettings,
  type UpdateSettingInput,
  type UpdateSystemSettingsInput,
} from '@features/settings/types';

interface AppSettingDto {
  id?: string;
  key?: string | null;
  value?: string | null;
  valueType?: number | string;
  category?: string | null;
  isPublic?: boolean;
  description?: string | null;
  updatedAt?: string;
  rowVersion?: string | null;
}

interface PublicSettingDto {
  key?: string | null;
  value?: string | null;
  valueType?: number | string;
  category?: string | null;
}

function mapAppSetting(d: AppSettingDto): AppSetting {
  return {
    id: d.id ?? '',
    key: d.key ?? '',
    value: d.value ?? '',
    valueType: settingValueTypeFromNumber(d.valueType),
    category: d.category ?? 'general',
    isPublic: d.isPublic ?? false,
    description: d.description ?? null,
    updatedAt: d.updatedAt ?? '',
    rowVersion: d.rowVersion ?? null,
  };
}

function mapPublicSetting(d: PublicSettingDto): PublicSetting {
  return {
    key: d.key ?? '',
    value: d.value ?? '',
    valueType: settingValueTypeFromNumber(d.valueType),
    category: d.category ?? 'general',
  };
}

interface ListQuery {
  category?: string;
  isPublic?: boolean;
}

export const settingsApi = {
  async list(query: ListQuery = {}): Promise<AppSetting[]> {
    const p = new URLSearchParams();
    if (query.category) p.set('category', query.category);
    if (query.isPublic !== undefined) p.set('isPublic', String(query.isPublic));
    const qs = p.toString();
    const res = await http.get<ActionResponse<AppSettingDto>>(
      `/api/v1/admin/settings${qs ? `?${qs}` : ''}`,
    );
    return unwrapList(res).items.map(mapAppSetting);
  },

  async getByKey(key: string): Promise<AppSetting> {
    const res = await http.get<ActionResponse<AppSettingDto>>(
      `/api/v1/admin/settings/${encodeURIComponent(key)}`,
    );
    return mapAppSetting(unwrap(res));
  },

  async create(input: CreateSettingInput): Promise<string> {
    const body = {
      key: input.key,
      value: input.value,
      valueType: SETTING_VALUE_TYPE_TO_NUMBER[input.valueType],
      category: input.category,
      isPublic: input.isPublic,
      description: input.description ?? null,
    };
    const res = await http.post<ActionResponse<string>>(
      '/api/v1/admin/settings',
      body,
    );
    return unwrap(res);
  },

  async update(key: string, input: UpdateSettingInput): Promise<void> {
    const body = {
      value: input.value,
      valueType: SETTING_VALUE_TYPE_TO_NUMBER[input.valueType],
      category: input.category,
      isPublic: input.isPublic,
      description: input.description ?? null,
      rowVersion: input.rowVersion ?? null,
    };
    const res = await http.put<ActionResponse<boolean>>(
      `/api/v1/admin/settings/${encodeURIComponent(key)}`,
      body,
    );
    unwrap(res);
  },

  async remove(key: string): Promise<void> {
    const res = await http.delete<ActionResponse<boolean>>(
      `/api/v1/admin/settings/${encodeURIComponent(key)}`,
    );
    unwrap(res);
  },

  async listPublic(): Promise<PublicSetting[]> {
    const res = await http.get<ActionResponse<PublicSettingDto>>(
      '/api/v1/public/settings',
    );
    return unwrapList(res).items.map(mapPublicSetting);
  },

  // -------- Legacy shim ---------------------------------------------------
  // Old call sites used `settingsApi.get()` to read a flat SystemSettings
  // record. We synthesise that from the new key-value list. NOTE: artık aktif
  // tüketici yok (HelpPage kaldırıldı) — ileride bu shim silinebilir.

  async get(): Promise<SystemSettings> {
    const items = await settingsApi.list();
    const map = new Map(items.map((s) => [s.key, s] as const));
    const pick = (k: string, fallback = ''): string => map.get(k)?.value ?? fallback;
    const pickBool = (k: string, fallback = false): boolean => {
      const v = map.get(k)?.value;
      if (v === undefined) return fallback;
      return v === 'true' || v === '1';
    };
    const pickNum = (k: string, fallback = 0): number => {
      const v = map.get(k)?.value;
      const n = v ? Number(v) : NaN;
      return Number.isFinite(n) ? n : fallback;
    };
    const latest = items.reduce<string>((acc, s) => (s.updatedAt > acc ? s.updatedAt : acc), '');
    return {
      siteName: pick('site.name', 'Emlak Katılım CMS'),
      contactEmail: pick('site.contactEmail', 'destek@emlakkatilim.test'),
      defaultLocale: pick('site.defaultLocale', 'tr'),
      timezone: pick('site.timezone', 'Europe/Istanbul'),
      brandColor: pick('site.brandColor', '#0ea5e9'),
      logoUrl: pick('site.logoUrl', '') || null,
      faviconUrl: pick('site.faviconUrl', '') || null,
      maintenanceMode: pickBool('site.maintenanceMode'),
      registrationOpen: pickBool('site.registrationOpen', true),
      forumEnabled: pickBool('site.forumEnabled', true),
      defaultPageSize: pickNum('site.defaultPageSize', 20),
      privacyPolicyPageId: pick('site.privacyPolicyPageId', '') || null,
      termsPageId: pick('site.termsPageId', '') || null,
      updatedAt: latest,
      updatedBy: '',
    };
  },

  /** Legacy bulk update — converts a partial SystemSettings into per-key PUTs. */
  async updateLegacy(_input: UpdateSystemSettingsInput): Promise<SystemSettings> {
    // We don't support bulk updates from the legacy shape any more. SettingsPage
    // now writes per-key. This is a best-effort no-op that re-reads BE state.
    return settingsApi.get();
  },
};
